import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Bug, Lightbulb, HelpCircle, Camera, Loader2, ExternalLink } from "lucide-react";

const TYPES = [
  { id: "bug", label: "Bug", icon: Bug, color: "#FF4D5E" },
  { id: "suggestion", label: "Suggerimento", icon: Lightbulb, color: "#FF9E1B" },
  { id: "question", label: "Domanda", icon: HelpCircle, color: "#26B5A8" },
];

// Compress an image File to a max width/quality and return a data URL.
async function compressImage(file, maxWidth = 1600, quality = 0.8) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    const reader = new FileReader();
    reader.onload = (e) => { i.src = e.target.result; };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const mime = file.type === "image/png" ? "image/jpeg" : (file.type || "image/jpeg");
  return canvas.toDataURL(mime, quality);
}

export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null); // data URL
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("hopzigo:open-feedback", handler);
    return () => window.removeEventListener("hopzigo:open-feedback", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.classList.add("hopzigo-modal-open");
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.classList.remove("hopzigo-modal-open");
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [open]);

  const reset = () => {
    setType("bug");
    setDescription("");
    setScreenshot(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      setScreenshot(dataUrl);
    } catch {
      toast.error("Impossibile leggere l'immagine");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 5) {
      toast.error("Scrivi una descrizione di almeno 5 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback", {
        type,
        description: description.trim(),
        page: location.pathname + location.search,
        screenshot_data_url: screenshot,
      });
      toast.success(
        <span className="inline-flex items-center gap-1">
          Grazie del feedback!{" "}
          {data.url && (
            <a href={data.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
              #{data.issue_number} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </span>
      );
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="feedback-modal">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !submitting && close()}
          />
          {/* Sheet */}
          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden anim-slide-up">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-[#ECEEF5]">
              <h2 className="font-heading text-xl font-bold text-[#1A1D29]">Invia feedback</h2>
              <button
                onClick={close}
                disabled={submitting}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#F5F6FA] text-[#5A6178]"
                aria-label="Chiudi"
                data-testid="feedback-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submit} className="px-5 py-4 space-y-4 max-h-[78vh] overflow-y-auto">
              {/* Type chips */}
              <div>
                <p className="block text-xs font-bold uppercase tracking-widest text-[#5A6178] mb-2">Tipo</p>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="feedback-type-group">
                  {TYPES.map(({ id, label, icon: Icon, color }) => {
                    const active = type === id;
                    return (
                      <button
                        type="button"
                        key={id}
                        onClick={() => setType(id)}
                        className={`relative px-2 py-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                          active ? "border-[#1A2A6C] bg-[#F5F6FA]" : "border-[#ECEEF5] bg-white hover:bg-[#F8F9FC]"
                        }`}
                        data-testid={`feedback-type-${id}`}
                        aria-pressed={active}
                      >
                        <Icon className="w-5 h-5" style={{ color }} />
                        <span className="text-xs font-semibold text-[#1A1D29]">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="fb-desc" className="block text-xs font-bold uppercase tracking-widest text-[#5A6178] mb-2">
                  Descrizione
                </label>
                <textarea
                  id="fb-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  maxLength={4000}
                  required
                  placeholder="Raccontaci cosa hai notato…"
                  className="w-full rounded-2xl border border-[#ECEEF5] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2A6C]/30 resize-y"
                  data-testid="feedback-description"
                />
                <p className="text-[11px] text-[#8A91A8] mt-1 text-right">{description.length}/4000</p>
              </div>

              {/* Screenshot */}
              <div>
                <p className="block text-xs font-bold uppercase tracking-widest text-[#5A6178] mb-2">
                  Screenshot <span className="text-[#8A91A8] font-normal normal-case lowercase">(opzionale)</span>
                </p>
                {screenshot ? (
                  <div className="relative">
                    <img
                      src={screenshot}
                      alt="anteprima"
                      className="w-full max-h-64 object-contain rounded-2xl border border-[#ECEEF5] bg-[#F8F9FC]"
                      data-testid="feedback-screenshot-preview"
                    />
                    <button
                      type="button"
                      onClick={() => { setScreenshot(null); if (fileRef.current) fileRef.current.value = ""; }}
                      className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-white shadow-md text-[#FF4D5E] border border-[#FFD1D6]"
                      aria-label="Rimuovi screenshot"
                      data-testid="feedback-remove-screenshot"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-[#ECEEF5] rounded-2xl cursor-pointer hover:bg-[#F8F9FC] text-[#5A6178] text-sm font-semibold"
                    data-testid="feedback-add-screenshot"
                  >
                    <Camera className="w-4 h-4" />
                    Aggiungi screenshot
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={onFileChange}
                      className="hidden"
                      data-testid="feedback-file-input"
                    />
                  </label>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full h-14 rounded-2xl bg-[#1A2A6C] text-white font-bold text-base shadow-[0_8px_24px_rgba(26,42,108,0.30)] hover:bg-[#14205A] disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="feedback-submit"
              >
                {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Invio…</>) : "Invia feedback"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
